import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import submit from "submit-form";
import Drawer from "./drawer";
import { getExerciseName } from "./modals/learn-more/blockHelpers";
import {
  buildSupersets,
  calculatePrefixForExercise,
} from "../utils/prefix-calculator";
import { PARAMETER_OPTIONS } from "../constants";
import { repsValue } from "../utils/reps-range";
import { rpeValue } from "../utils/rpe-range";
import { round } from "lodash";
import { getDisplayDate } from "../utils/date-helpers";
import generateRandomId from "../utils/generate-random-id";
import { groupExercisesByComplex } from "../utils/complex";

const ExerciseClipboardContent = ({ query, locals }) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [exercisesToCopy, setExercisesToCopy] = useState(null);

  /**
   * Get the exercises stored in the local storage
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      const exerciseInLocalStorage = localStorage.getItem("exercisesToCopy");
      setExercisesToCopy(
        exerciseInLocalStorage ? JSON.parse(exerciseInLocalStorage) : null
      );
    }
  }, [query]);

  if (!exercisesToCopy) return null;
  const exercises = groupExercisesByComplex(exercisesToCopy);
  return (
    <div className="clipboard-content">
      <button
        onClick={() => {
          setOpen(true);
        }}
      >
        <span className="clipboard-text">{t("clipboard")}</span>
        <img
          src="/img/new-icons/horizontal-clipboard-text.svg"
          alt="Paste Icon"
        />
      </button>
      <Drawer
        show={open}
        close={() => {
          setOpen(false);
        }}
        title={t("clipboard")}
      >
        <DrawerClipboardContent
          setOpen={setOpen}
          setExercisesToCopy={setExercisesToCopy}
          exercises={exercises}
          locals={locals}
        />
      </Drawer>
    </div>
  );
};
export default ExerciseClipboardContent;

const DrawerClipboardContent = ({
  setOpen,
  setExercisesToCopy,
  exercises = [],
  locals,
}) => {
  const { t } = useTranslation("common");
  const { activeUserToRequest, withWeight, fromMetric } = locals;
  const { intensityMeasure = "rpe", referAsClients = true } =
    activeUserToRequest;
  const referAsClientsText = referAsClients ? `client's` : `athlete's`;
  const isRiR = intensityMeasure.toLowerCase() === "rir";
  const intensityMeasureToShow = isRiR ? "RiR" : "RPE";
  const supersets = buildSupersets(exercises);
  const dateToDisplay =
    exercises[0]?.utcDate && !exercises[0]?.utcDate.startsWith("1970")
      ? exercises[0].utcDate
      : new Date().toISOString();

  const handleDiscard = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem("exercisesToCopy");
      localStorage.removeItem("originalAthleteLibraryToCopy");
      setOpen(false);
      setExercisesToCopy(null);
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          exercisesInClipboard: "null",
        },
      });
    }
  };

  return (
    <div className="clipboard-main-section">
      <div className="clipboard-body">
        <div className="chip info clipboard-chip">{t("pasteIntoWorkout")}</div>
        <div className="workout-day">
          <header>
            <p className="s1">{getDisplayDate(dateToDisplay, true)}</p>
          </header>
          <section>
            {exercises.map(
              ({
                _id,
                type,
                sets = [],
                primary,
                parameters,
                secondary,
                complexName,
                subType,
              }) => {
                const isAssistant = type === "assistant";
                const hasFreeForm = sets.some((set) => set?.freeform);
                const isExerciseStarter =
                  isAssistant && primary === "assistant" && !parameters;
                const prefix = calculatePrefixForExercise(
                  _id,
                  supersets
                ).replace(")", "");
                const setsWithIds = sets.map((set) => ({
                  ...set,
                  id: generateRandomId(),
                }));
                return (
                  <div key={_id} className="clipboard-exercise-main">
                    <div className="title-exercise">
                      <span className="calendar-prefix">{prefix}</span>
                      <p className="font-semibold text-(--font-size-md)">
                        {getExerciseName({
                          type,
                          primary,
                          secondary,
                          complexName,
                          subType,
                        })}
                      </p>
                    </div>
                    {isAssistant && hasFreeForm ? (
                      <DisplayAssistantContent sets={setsWithIds} />
                    ) : (
                      <DisplayPrimaryOrSecondaryContent
                        isAssistant={isAssistant}
                        isExerciseStarter={isExerciseStarter}
                        isRiR={isRiR}
                        sets={setsWithIds}
                        parameters={parameters}
                        intensityMeasureToShow={intensityMeasureToShow}
                        withWeight={withWeight}
                        fromMetric={fromMetric}
                      />
                    )}
                  </div>
                );
              }
            )}
          </section>
        </div>
      </div>
      <div className="clipboard-footer-content">
        <div className="clipboard-explain">
          <span>{t("loadRecalculate", { referral: referAsClientsText })}</span>
        </div>
        <div className="clipboard-button-container">
          <button onClick={handleDiscard}>{t("discardCopy")}</button>
        </div>
      </div>
    </div>
  );
};

const DisplayAssistantContent = ({ sets }) => {
  return (
    <div className="assistant-container">
      {sets.map(({ id, freeform = "" }, idx) => (
        <div className="set-row" key={`${id}-${idx}`}>
          {freeform}
        </div>
      ))}
    </div>
  );
};

const DisplayPrimaryOrSecondaryContent = ({
  isAssistant,
  isExerciseStarter,
  isRiR,
  sets,
  parameters,
  intensityMeasureToShow,
  withWeight,
  fromMetric,
}) => {
  const { RPE, RPEAMRAP, LOAD, REPS } = PARAMETER_OPTIONS;

  return (
    <div>
      <table className="tr-table">
        <thead>
          {!isAssistant || isExerciseStarter ? (
            <tr>
              <th>Sets</th>
              <th>Reps</th>
              <th>{isRiR ? "RiR" : "RPE"}</th>
              <th>
                <i>Load(*)</i>
              </th>
              {isExerciseStarter ? null : <th>%1RM</th>}
            </tr>
          ) : (
            <tr>
              {parameters?.map((param, idx) => (
                <th key={`${idx}-${param || ""}`}>
                  {[RPE, RPEAMRAP].includes(param) ? (
                    intensityMeasureToShow
                  ) : param === "Load" ? (
                    <i>Load(*)</i>
                  ) : (
                    param
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {!isAssistant || isExerciseStarter
            ? sets?.map((set, idx) => {
                const paramsRow = {
                  set: set,
                  idx: `${idx}`,
                  withWeight,
                  fromMetric,
                  isExerciseStarter,
                  isRiR,
                };
                return <TableRow {...paramsRow} key={`${set?.id}-${idx}`} />;
              })
            : sets?.map((set, idx) => {
                const paramsRow = {
                  idx: `${idx}`,
                  parameters: parameters,
                  set,
                  withWeight,
                  fromMetric,
                  isRiR,
                  RPE,
                  RPEAMRAP,
                  LOAD,
                  REPS,
                };
                return (
                  <TableForAssistant {...paramsRow} key={`${set?.id}-${idx}`} />
                );
              })}
        </tbody>
      </table>
    </div>
  );
};

const TableRow = ({
  set,
  idx,
  withWeight,
  fromMetric,
  isExerciseStarter = false,
  isRiR,
}) => {
  const { sets, reps, rpe, rpeLower, load, percentRepMax, repsLower, id } = set;

  return (
    <tr key={`${id}-${idx}`}>
      <td className="sets">{sets}</td>
      <td>{repsValue(reps, repsLower)}</td>
      <td>{rpeValue(rpe, rpeLower, isRiR)}</td>
      <td>{load > 0 ? `${withWeight(fromMetric(load))}` : "-"}</td>
      {isExerciseStarter ? null : (
        <td className="intensity">
          {percentRepMax > 0 ? `${round(percentRepMax, 1)}%` : "-"}
        </td>
      )}
    </tr>
  );
};

const TableForAssistant = ({
  idx,
  parameters,
  set,
  isRiR,
  withWeight,
  fromMetric,
  RPE,
  RPEAMRAP,
  LOAD,
  REPS,
}) => {
  const { reps, rpe, rpeLower, load, repsLower, id } = set;

  const getCellValue = (_paramToUse) => {
    const param = (_paramToUse || "").toLowerCase();
    if ([RPE, RPEAMRAP, "rpe/amrap"].includes(param)) {
      return rpeValue(rpe, rpeLower, isRiR);
    }
    if (_paramToUse === LOAD && load > 0) {
      return withWeight(fromMetric(load));
    }
    if (_paramToUse === REPS) {
      return repsValue(reps, repsLower);
    }
    return set[param];
  };

  return (
    <tr key={`${id}-${idx}`}>
      {parameters?.map((_param, idy) => (
        <td key={`${idy}-${_param || ""}`}>{getCellValue(_param)}</td>
      ))}
    </tr>
  );
};

ExerciseClipboardContent.propTypes = {
  query: PropTypes.string,
  locals: PropTypes.object,
};

DrawerClipboardContent.propTypes = {
  setOpen: PropTypes.func,
  setExercisesToCopy: PropTypes.func,
  exercises: PropTypes.array,
  locals: PropTypes.object,
};

DisplayAssistantContent.propTypes = {
  sets: PropTypes.array,
};

DisplayPrimaryOrSecondaryContent.propTypes = {
  isAssistant: PropTypes.bool,
  isExerciseStarter: PropTypes.bool,
  isRiR: PropTypes.bool,
  sets: PropTypes.array,
  parameters: PropTypes.array,
  intensityMeasureToShow: PropTypes.string,
  withWeight: PropTypes.any,
  fromMetric: PropTypes.any,
};

TableRow.propTypes = {
  set: PropTypes.object,
  idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  withWeight: PropTypes.any,
  fromMetric: PropTypes.any,
  isExerciseStarter: PropTypes.bool,
  isRiR: PropTypes.bool,
};

TableForAssistant.propTypes = {
  set: PropTypes.object,
  idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parameters: PropTypes.array,
  isRiR: PropTypes.bool,
  withWeight: PropTypes.any,
  fromMetric: PropTypes.any,
  RPE: PropTypes.any,
  RPEAMRAP: PropTypes.any,
  LOAD: PropTypes.any,
  REPS: PropTypes.any,
};
